/**
 * Maintenance Schedules View
 * Admin-facing view for managing maintenance schedules
 * 
 * Features:
 * - DataGrid with all schedules
 * - Filter by client, status (active/inactive)
 * - Create/Edit/Delete schedules via dialog
 * - View upcoming jobs for a schedule
 * - Manual job generation
 * - Stats overview
 */

import React, { useContext, useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  PlayArrow as GenerateIcon
} from '@material-ui/icons';
import { MaintenanceContext } from '../contexts/MaintenanceContext';
import ManageSchedule from '../components/ManageSchedule';
import moment from 'moment';

const MaintenanceSchedules = () => {
  const {
    schedules,
    stats,
    isLoading,
    loadSchedules,
    loadScheduleDetails,
    deleteSchedule,
    generateJobs,
    loadStats,
    selectedSchedule,
    setSelectedSchedule,
    notifyClient
  } = useContext(MaintenanceContext);

  const [clientFilter, setClientFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [notifyFilter, setNotifyFilter] = useState('all'); // all | yes | no

  // Get unique clients for filter
  const clients = [...new Set(schedules.map(s => s.client_name))].sort();

  // Load schedules and stats on mount
  useEffect(() => {
    loadSchedules();
    loadStats();
  }, []);

  // Filter schedules
  const filteredSchedules = schedules.filter(schedule => {
    const matchesClient = clientFilter === 'all' || schedule.client_name === clientFilter;
    const matchesStatus = statusFilter === 'all' || (schedule.status === statusFilter);
    return matchesClient && matchesStatus;
  });

  // Handle create new schedule
  const handleCreate = () => {
    setSelectedSchedule(null);
    setEditMode(false);
    setManageDialogOpen(true);
  };

  // Handle edit schedule
  const handleEdit = async (scheduleId) => {
    await loadScheduleDetails(scheduleId);
    setEditMode(true);
    setManageDialogOpen(true);
  };

  // Handle delete schedule - show confirmation
  const handleDeleteClick = (schedule) => {
    setScheduleToDelete(schedule);
    setDeleteConfirmOpen(true);
  };

  // Handle delete schedule - confirmed
  const handleDeleteConfirm = async () => {
    if (scheduleToDelete) {
      const success = await deleteSchedule(scheduleToDelete.id);
      if (success) {
        setDeleteConfirmOpen(false);
        setScheduleToDelete(null);
        loadSchedules();
        loadStats();
      }
    }
  };

  // Handle view details
  const handleViewDetails = async (scheduleId) => {
    await loadScheduleDetails(scheduleId);
    setDetailsDialogOpen(true);
  };

  // Handle manual job generation
  const handleGenerateJobs = async () => {
    const success = await generateJobs();
    if (success) {
      loadStats();
    }
  };

  // Get frequency label
  const getFrequencyLabel = (frequency) => {
    return frequency.charAt(0).toUpperCase() + frequency.slice(1);
  };

  // Get recurrence description
  const getRecurrenceDescription = (schedule) => schedule.recurrence_description || schedule.frequency;

  // DataGrid columns
  const columns = [
    {
      field: 'title',
      headerName: 'Title',
      width: 250,
      flex: 1
    },
    {
      field: 'client_name',
      headerName: 'Client',
      width: 180
    },
    {
      field: 'asset_name',
      headerName: 'Asset',
      width: 150
    },
    {
      field: 'assigned_engineer_name',
      headerName: 'Assigned To',
      width: 180,
      valueGetter: (params) => params.row.assigned_engineer_name || '—'
    },
    {
      field: 'frequency',
      headerName: 'Frequency',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={getFrequencyLabel(params.value)}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'recurrence',
      headerName: 'Recurrence',
      width: 220,
      valueGetter: (params) => getRecurrenceDescription(params.row)
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={(params.row.status || 'active').toUpperCase()}
          color={(params.row.status === 'active') ? 'primary' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewDetails(params.row.id)}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEdit(params.row.id)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => handleDeleteClick(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <div style={{ padding: 20 }}>
      {/* Stats Cards */}
      <Grid container spacing={2} style={{ marginBottom: 20 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Schedules
              </Typography>
              <Typography variant="h4">
                {stats?.activeSchedules || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending Jobs
              </Typography>
              <Typography variant="h4">
                {stats?.pendingJobs || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Overdue Jobs
              </Typography>
              <Typography variant="h4" style={{ color: '#f44336' }}>
                {stats?.overdueJobs || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completion Rate
              </Typography>
              <Typography variant="h4">
                {stats?.completionRate ? `${Math.round(stats.completionRate)}%` : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Maintenance Schedules
        </Typography>
        
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Client</InputLabel>
            <Select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              label="Client"
            >
              <MenuItem value="all">All Clients</MenuItem>
              {clients.map(client => (
                <MenuItem key={client} value={client}>{client}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
          
          <Tooltip title="Generate Jobs">
            <IconButton
              onClick={handleGenerateJobs}
              disabled={isLoading}
              color="primary"
            >
              <GenerateIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => {
                loadSchedules();
                loadStats();
              }}
              disabled={isLoading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            New Schedule
          </Button>
        </Box>
      </Box>

      {/* Schedules DataGrid */}
      <Paper style={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredSchedules}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          loading={isLoading}
          disableSelectionOnClick
          density="comfortable"
        />
      </Paper>

      {/* Manage Schedule Dialog */}
      <ManageSchedule
        open={manageDialogOpen}
        onClose={() => {
          setManageDialogOpen(false);
          setSelectedSchedule(null);
        }}
        onSaved={() => {
          setManageDialogOpen(false);
          setSelectedSchedule(null);
          loadSchedules();
          loadStats();
        }}
        editMode={editMode}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the schedule "{scheduleToDelete?.title}"?
          </Typography>
          <Typography color="textSecondary" style={{ marginTop: 10 }}>
            This will not delete existing jobs, but no new jobs will be created for this schedule.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="secondary"
            variant="contained"
            disabled={isLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedSchedule?.schedule?.title || 'Schedule Details'}
        </DialogTitle>
        
        <DialogContent dividers>
          {selectedSchedule?.schedule ? (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Client:</strong> {selectedSchedule.schedule.client_name}
              </Typography>
              
              {selectedSchedule.schedule.asset_name && (
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Asset:</strong> {selectedSchedule.schedule.asset_name}
                </Typography>
              )}
              
              <Typography variant="subtitle1" gutterBottom>
                <strong>Frequency:</strong> {getFrequencyLabel(selectedSchedule.schedule.frequency)}
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom>
                <strong>Recurrence:</strong> {getRecurrenceDescription(selectedSchedule.schedule)}
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom>
                <strong>Status:</strong>{' '}
                <Chip
                  label={selectedSchedule.schedule.is_active ? 'Active' : 'Inactive'}
                  color={selectedSchedule.schedule.is_active ? 'primary' : 'default'}
                  size="small"
                />
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom>
                <strong>Description:</strong>
              </Typography>
              <Typography variant="body2" paragraph>
                {selectedSchedule.schedule.description || 'No description provided'}
              </Typography>
              
              {selectedSchedule.schedule.estimated_downtime_minutes && (
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Estimated Downtime:</strong> {selectedSchedule.schedule.estimated_downtime_minutes} minutes
                </Typography>
              )}
              
              {selectedSchedule.schedule.requires_client_notification && (
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Requires Client Notification:</strong> Yes
                </Typography>
              )}
              
              {/* Jobs with notify filter */}
              {(() => {
                let jobs = selectedSchedule.jobs || selectedSchedule.upcomingJobs || [];
                if (notifyFilter !== 'all') {
                  const wantNotified = notifyFilter === 'yes';
                  jobs = jobs.filter(j => Boolean(j.client_notified_at) === wantNotified);
                }
                if (!jobs.length) return null;
                return (
                <Box mt={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Jobs:</strong>
                  </Typography>
                  <Box mb={1} display="flex" alignItems="center" gridGap={8}>
                    <Typography variant="body2">Notified:</Typography>
                    <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                      <Select value={notifyFilter} onChange={(e) => setNotifyFilter(e.target.value)}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  {jobs.slice(0, 5).map(job => (
                    <Box key={job.id} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">
                        • {moment(job.scheduled_date).format('MMM DD, YYYY')} - {job.status}
                        {job.intended_notification_email && (
                          <span style={{ marginLeft: 8, color: '#666' }}>
                            (to {job.intended_notification_email})
                          </span>
                        )}
                      </Typography>
                      <Box>
                        {job.client_notified_at ? (
                          <Chip label="Client Notified" size="small" color="primary" />
                        ) : (
                          selectedSchedule.schedule.notify_client && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={async () => {
                                const ok = await notifyClient(job.id);
                                if (ok) await loadScheduleDetails(selectedSchedule.schedule.id);
                              }}
                              disabled={isLoading}
                            >
                              Notify Client
                            </Button>
                          )
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
                );
              })()}
            </Box>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MaintenanceSchedules;
